export const mixinBlind = `
  overflow: hidden;
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  clip: rect(0 0 0 0);
`;

export const mixinBlindScroll = `
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const mixinScrollX = `
  overflow: hidden;
  overflow-x: auto;
  white-space: nowrap;

  ${mixinBlindScroll};
`;

export const mixinSafeArea = (property: string, value = 0) => `
  ${property}: ${value}px;
  ${property}: ${
    value ? `calc(env(safe-area-inset-bottom) + ${value}px)` : 'env(safe-area-inset-bottom)'
  };
  ${property}: ${
    value
      ? `calc(constant(safe-area-inset-bottom) + ${value}px)`
      : 'constant(safe-area-inset-bottom)'
  };
`;
type PlaceholderStyleKey = 'color' | 'font-weight' | 'opacity';
export const mixinPlaceholder = (css: Partial<Record<PlaceholderStyleKey, string>>) => {
  const parsedCss = Object.entries(css || {}).reduce((acc, [key, value]) => {
    acc = `${acc}${key}:${value};`;
    return acc;
  }, '');

  return `
  &::-webkit-input-placeholder {
    ${parsedCss}
  }
  &:-moz-placeholder {
    ${parsedCss}
  }
  &:-ms-input-placeholder {
    ${parsedCss}
  }
  &::placeholder {
    ${parsedCss}
  }
`;
};

export const mixinEllipsis = (line = 1, isPreWrap = false) => `
  ${
    line === 1
      ? `overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;`
      : `display: block;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: ${isPreWrap ? 'pre-line' : 'normal'};
    word-wrap: break-word;
    -webkit-line-clamp: ${line};
    -webkit-box-orient: vertical;`
  }
`;
interface IKeyFrames {
  [key: string]: string;
}
const keyFramesObj: IKeyFrames = {
  roundAll: `@keyframes roundAll {
    from {
      transform: translateX(0%);
    }
    to {
      transform: translateX(100%);
    },
  }`,
  fadeIn: `@keyframes fadeIn  {
    0% {
      opacity: 0
    }
    100% {
      opacity: 1
    },
  }`,
  appearTopSheet: `@keyframes appearTopSheet {
    0% {
      transform: translate(-50%, -50px);
      opacity: 0;
    },
    100% {
      transform: translate(-50%, 0);
      opacity: 1;
    },
  }`,
  disappearTopSheet: `@keyframes disappearTopSheet {
    0% {
      transform: translate(-50%, 0);
      opacity: 1;
    },
    100% {
      transform: translate(-50%, -30px);
      opacity: 0;
    },
  }`,
  fadeOut: `@keyframes fadeOut {
    0% {
      opacity: 1
    }
    100% {
      opacity: 0
    }
  }`,
  pulse: `@keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }`,
  tooltipInSlide: `@keyframes tooltipInSlide {
    100% {
      transform: translate3d(0, 0, 0)
    }
  }`,
  tooltipOutSlide: `@keyframes tooltipOutSlide {
    0% {
      opacity: 1;
      visibility: visible;
      transform: translate3d(0, 0, 0)
    }
    99.9% {
      transform: translate3d(0, 0, 0)
    }
    100% {
      opacity: 0;
      visibility: hidden
    }
  }`,
  likeActive: `@keyframes likeActive {
    0%{
      transform: scale(1.2)
    }
    100% {
      transform: scale(1)
    }
  }`,
  skeleton: `@keyframes skeleton {
    0% {
      transform: translateX(-100%)
    }
    100% {
      transform: translateX(100%)
    }
  }`,
};

export const mixinKeyFrames = (key: string, options: string) => {
  const keyframesStyle = `
    ${keyFramesObj[key]};
    animation: ${key} ${options};
  `;
  return keyframesStyle;
};

/**
 * @param value border-radius 값
 * @returns border-radius css
 */
export const mixinCarouselListBorder = (row = 1, value = '4') => `
${
  row === 1
    ? `&:first-child {
        border-top-left-radius: ${value}px;
        border-bottom-left-radius: ${value}px;
      }
      &:last-child {
        border-top-right-radius: ${value}px;
        border-bottom-right-radius: ${value}px;
      }
      &:only-child {
        border-radius: ${value}px;
      }`
    : row === 2
    ? `&:nth-child(1) {
        border-top-left-radius: ${value}px;
      }
      &:nth-child(2) {
        border-bottom-left-radius: ${value}px;
      }
      &:nth-last-child(2) {
        border-top-right-radius: ${value}px;
      }
      &:nth-last-child(1) {
        border-bottom-right-radius: ${value}px;
      }`
    : ''
}
`;

/**
 * 검색 칩 텍스트 공통 스타일
 * @description 검색칩 텍스트의 고정된 스타일 반환
 * - 모바일에서만 max-width 및 말줄임 적용
 * - 칩 1개: max-width 106px
 * - 칩 2개 이상: max-width 69px (부모가 :not(:only-child)일 때 자동 적용)
 * @param isMobile - 모바일 환경 여부 (기본값: true)
 */
export const mixinChipText = (isMobile = true) => `
  font-size: 11px;
  font-weight: 500;
  line-height: 18px;
  ${
    isMobile &&
    `
    display: inline-block;
    max-width: 106px;
    ${mixinEllipsis()};

    *:not(:only-child) > & {
      max-width: 69px;
    }
  `
  }
`;
